/**
 * Labels, defaults and input config for the free-shipping threshold calculator.
 *
 * English-only by design: the tool lives under /tools, which ships to the
 * English locales only (see EN_ONLY_SLUGS). Currency is the one locale-varying
 * piece and is resolved at render time from the request locale, not hardcoded
 * here.
 */

export const STRINGS = {
  toolTitle: "Your numbers",
  aov: "Current average order value",
  shipping: "Shipping cost per order",
  margin: "Gross margin",
  ownThreshold: "Or set your own threshold",
  recommended: "Recommended",
  reset: "Reset to recommended",
  covered: "Covered",
  notCovered: "Not covered",
  // Support rows
  currentAov: "Current average order value",
  extraSpend: "Extra spend to reach threshold",
  marginOnExtra: "Margin on that extra spend",
  shippingCovered: "Shipping cost to cover",
  recommendedThreshold: "Recommended threshold",
  disclaimer:
    "Estimates only, based on the numbers you enter. This assumes your gross margin applies to the extra spend a customer adds to reach the threshold, and doesn't account for customers already above it, returns or the effect of the offer on demand.",
} as const;

/** Input ranges and step sizes. Inputs are clamped to these bounds. */
export const RANGES = {
  // Money fields accept a wide range; the ceiling just guards against overflow.
  money: { min: 0, max: 1_000_000, step: 1 },
  // Margin slider stops at 99%: a 100% margin means zero cost, which isn't a
  // real product, and stopping short keeps the slider in the range founders
  // actually use while still reaching the thin-margin zone.
  margin: { min: 0, max: 99, step: 1 },
} as const;

/**
 * Neutral, round placeholder values the user is meant to change. Deliberately
 * not derived from any Wayflyer merchant data. AOV 60 / shipping 6 / margin 60%
 * yields a clean £70 threshold and a ~17% uplift on load, so the tool shows a
 * sensible non-trivial result without looking inert.
 */
export const DEFAULTS = {
  aov: 60,
  shipping: 6,
  marginPct: 60,
} as const;

export interface FaqQuestion {
  question: string;
  answer: string;
}

export const FAQ_TITLE = "Free-shipping threshold FAQ";

export const FAQ_QUESTIONS: FaqQuestion[] = [
  {
    question: "What is a free-shipping threshold?",
    answer:
      "It's the order value above which you offer free shipping, for example \"free shipping over $60.\" Customers below it pay for delivery; customers at or above it don't, which is why the threshold needs to sit high enough that the extra margin from bigger baskets covers the shipping cost you absorb.",
  },
  {
    question: "How do you calculate a free-shipping threshold?",
    answer:
      "The standard formula is your shipping cost divided by your gross margin, added to your current average order value: **threshold = AOV + (shipping cost / margin)**. That's exactly what this tool works out, then rounds up to a clean number you can actually advertise.",
  },
  {
    question: "What is average order value (AOV)?",
    answer:
      "Average order value is your total revenue divided by your number of orders over a period, the typical amount a customer spends in one purchase. It's the starting point for this calculator, since the recommended threshold is set relative to it rather than as a flat number.",
  },
  {
    question: "Does free shipping actually increase sales?",
    answer:
      "It's one of the most consistently effective incentives in eCommerce for lifting conversion and average order value, since customers routinely add items to a basket just to clear a free-shipping threshold. The catch is that it only helps your bottom line if the threshold is set high enough to cover what you're giving away, which is what this tool checks before you offer it.",
  },
  {
    question:
      "How much does my average basket need to grow to hit the threshold?",
    answer:
      "This tool shows the extra spend needed on top of your current AOV to reach the threshold, plus that same figure as a percentage uplift, so you can judge whether it's a realistic ask for your typical customer or a stretch.",
  },
  {
    question:
      "Does this account for customers already above the threshold, returns or demand effects?",
    answer:
      "No. The calculation assumes your gross margin applies evenly to the extra spend needed to reach the threshold, and doesn't account for customers who were already spending above it, returns, or how the offer itself might change buying behaviour. Treat the result as your cost-covering floor, not a full profit forecast.",
  },
  {
    question: "Where can I get help with the rest of my Black Friday offer?",
    answer:
      "Check the margin on a bundle with the [bundle pricing calculator](/tools/bundle-pricing), or the maths on a straight discount with the [discount break-even calculator](/tools/discount-break-even). Browse every tool in the [full Black Friday toolkit](/tools).",
  },
];
